import { t } from "i18next"
import Card from "@/components/card"
import riskStore from "@/features/risks/entities/risk/model/risk.store"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import ListItem from "@/components/list-item"
import Button from "@/components/button"
import SaveToast from "@/components/SaveToast"
import { faCheckCircle, faChevronDown, faChevronUp, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons"
import jobStore from "../../job/model/job.store"
import unitStore from "../../unit/model/unit.store"
import JobsChips from "../../job/model/ui/JobsChips"
import { useEffect, useMemo, useState } from "react"
import Checkbox from "@/components/checkbox"
import ListCell from "@/components/list-cell"
import ListButton from "@/components/list-button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useNavigate, useLocation } from "react-router-dom"

export function RisksAddFromJobs() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const unit = useAppSelector(unitStore.selectors.active)
  const jobs = useAppSelector(jobStore.selectors.list)
  const status = useAppSelector(riskStore.selectors.status)

  const [selectedOrigins, setSelectedOrigins] = useState<Record<string, string[]>>({})
  const hasSelectedOrigins = Object.values(selectedOrigins).some((arr) => arr.length > 0)
  const [isSaving, setIsSaving] = useState(false)

  const openJobsDefault = useMemo(() => {
    const result: Record<string, boolean> = {}
    unit.JobIds.forEach((id) => {
      result[id] = false
    })
    return result
  }, [unit.JobIds])

  const [openJobs, setOpenJobs] = useState<Record<string, boolean>>(openJobsDefault)

  useEffect(() => {
    setOpenJobs(openJobsDefault)
  }, [openJobsDefault])

  const toggleOrigin = (riskTitle: string, origin: string) => {
    setSelectedOrigins((prev) => {
      const current = prev[riskTitle] || []
      const exists = current.includes(origin)

      // Si déjà sélectionné, déselectionner
      if (exists) {
        return {
          ...prev,
          [riskTitle]: []
        }
      }

      // Sinon, déselectionner TOUS et sélectionner celui-ci uniquement
      const allCleared: Record<string, string[]> = {}
      Object.keys(prev).forEach((key) => {
        allCleared[key] = []
      })
      return {
        ...allCleared,
        [riskTitle]: [origin]
      }
    })
  }

  const toggleJob = (jobId: string) => {
    setOpenJobs((prev) => ({
      ...prev,
      [jobId]: !prev[jobId]
    }))
  }

  const handleSave = async () => {
    if (!unit) return

    const riskToCreate = Object.entries(selectedOrigins).find(([, origins]) => origins.length > 0)
    if (!riskToCreate) return

    const [riskTitle, [origin]] = riskToCreate
    const { backTo } = (location.state as any) || {}

    try {
      setIsSaving(true)
      dispatch(riskStore.actions.setStatus("STATUS_LOADING"))

      const createdRisk = await dispatch(
        riskStore.thunks.createFromJob({
          unitId: unit.id,
          title: riskTitle,
          origin
        })
      ).unwrap()

      dispatch(riskStore.actions.setStatus("STATUS_LOADED"))
      if (createdRisk?.id) {
        navigate(`/risks/risk/${createdRisk.id}`, { state: { backTo, showSuccessModal: true } })
      } else {
        navigate(backTo || "/risks")
      }
    } catch (e) {
      console.error("❌ save error", e)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="h-full">
      <Card
        className="overflow-hidden"
        title={
          <div className="flex justify-between items-center w-full">
            <div className="flex flex-wrap items-center gap-4">
              <span className="whitespace-nowrap">{t("Risques proposés selon les métiers sélectionnés")}</span>
              <JobsChips className="text-sm" deletable={false} />
            </div>
            <span className="text-sm grey-t font-normal">{t("Sélectionnez 1 seule origine")}</span>
          </div>
        }>
        <div className="card-content-list">
          {unit.JobIds.map((id) => {
            const job = jobs.find((j) => j.id === id)
            if (!job) return null

            return (
              <ListItem className="flex-col" key={id}>
                <div className="flex items-center gap-1 cursor-pointer" onClick={() => toggleJob(job.id)}>
                  <ListButton icon={openJobs[job.id] ? faChevronUp : faChevronDown} onClick={() => toggleJob(job.id)} />
                  <h3 className="my-0 font-semibold text-sm">{job.jobName}</h3>
                </div>

                {openJobs[job.id] && (
                  <div className="flex flex-col gap-3 mt-2 ml-8">
                    {job.risks.map((risk, index) => (
                      <div key={`${risk.title}-${index}`} className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                        <h3 className="flex items-center font-semibold text-sm text-gray-800 gap-2 mb-4">
                          <FontAwesomeIcon icon={faTriangleExclamation} className="grey-t" size="sm" />
                          <span>{risk.title}</span>
                        </h3>

                        {risk.origins.length > 0 && (
                          <div className="flex flex-col gap-2">
                            {risk.origins.map((o, index) => {
                              const isChecked = selectedOrigins[risk.title]?.includes(o) ?? false

                              return (
                                <div
                                  key={`${o}-${index}`}
                                  className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
                                  onClick={() => toggleOrigin(risk.title, o)}>
                                  <ListCell>
                                    <Checkbox active={isChecked} onClick={() => toggleOrigin(risk.title, o)} />
                                  </ListCell>
                                  <span className="text-sm text-gray-700">{o}</span>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ListItem>
            )
          })}
        </div>
      </Card>

      {hasSelectedOrigins && (
        <SaveToast isSaveActive status={status} onCancel={() => setSelectedOrigins({})}>
          <Button className="primary" icon={faCheckCircle} onClick={handleSave} isWithBorder isLoading={isSaving}>
            Continuer
          </Button>
        </SaveToast>
      )}
    </div>
  )
}
